import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  format, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek,
  startOfYear,
  endOfYear,
  subWeeks,
  subYears
} from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/dateTime';
import {
  BarChart3,
  Download,
  Filter,
  Users,
  FolderKanban,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Table,
  LayoutList,
  CalendarDays,
  CalendarRange,
  Receipt,
  CircleDollarSign,
  CircleDot,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { UserSelect } from '../components/UserSelect';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MonthlyReportsDialog from '../components/reports/MonthlyReportsDialog';

export default function ReportsPage() {
  const { api } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [detailedReport, setDetailedReport] = useState(null);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'table'
  const [groupBy, setGroupBy] = useState('project'); // 'project', 'user', 'date'
  const [monthlyOpen, setMonthlyOpen] = useState(false); // F11/F12 Monatsauswertungen (PDF)

  // Period presets
  const [periodPreset, setPeriodPreset] = useState('month');

  const [filters, setFilters] = useState({
    start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end_date: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    user_id: '',
    customer_id: '',
    project_id: '',
    billing_status: '',  // '', 'open', 'billed'
  });

  // Period presets configuration
  const periodPresets = useMemo(() => {
    const today = new Date();
    return {
      today: {
        label: 'Heute',
        start: format(today, 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      },
      week: {
        label: 'Diese Woche',
        start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      },
      lastWeek: {
        label: 'Letzte Woche',
        start: format(startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      },
      month: {
        label: 'Dieser Monat',
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(endOfMonth(today), 'yyyy-MM-dd'),
      },
      lastMonth: {
        label: 'Letzter Monat',
        start: format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'),
        end: format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'),
      },
      year: {
        label: 'Dieses Jahr',
        start: format(startOfYear(today), 'yyyy-MM-dd'),
        end: format(endOfYear(today), 'yyyy-MM-dd'),
      },
      lastYear: {
        label: 'Letztes Jahr',
        start: format(startOfYear(subYears(today, 1)), 'yyyy-MM-dd'),
        end: format(endOfYear(subYears(today, 1)), 'yyyy-MM-dd'),
      },
      custom: {
        label: 'Benutzerdefiniert',
        start: filters.start_date,
        end: filters.end_date,
      },
    };
  }, [filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchDetailedReport();
  }, [filters, groupBy]);

  // Apply period preset
  useEffect(() => {
    if (periodPreset !== 'custom' && periodPresets[periodPreset]) {
      const preset = periodPresets[periodPreset];
      setFilters(prev => ({
        ...prev,
        start_date: preset.start,
        end_date: preset.end,
      }));
    }
  }, [periodPreset]);

  const fetchInitialData = async () => {
    try {
      const [usersRes, customersRes, projectsRes] = await Promise.all([
        api.get('/users'),
        api.get('/customers'),
        api.get('/projects'),
      ]);
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };

  const fetchDetailedReport = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.billing_status) params.append('billing_status', filters.billing_status);
      params.append('group_by', groupBy);

      const response = await api.get(`/reports/detailed?${params.toString()}`);
      setDetailedReport(response.data);
      
      // Auto-expand first group
      if (response.data.grouped_data?.length > 0) {
        const firstKey = groupBy === 'date' 
          ? response.data.grouped_data[0].date 
          : groupBy === 'project' 
            ? response.data.grouped_data[0].project_id 
            : response.data.grouped_data[0].user_id;
        setExpandedGroups({ [firstKey]: true });
      }
    } catch (error) {
      console.error('Failed to fetch detailed report:', error);
      toast.error('Fehler beim Laden der Berichte');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.project_id) params.append('project_id', filters.project_id);

      const response = await api.get(`/reports/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projektzeit_export_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Export erfolgreich');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Fehler beim Exportieren');
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDate = (dateStr) => {
    return safeFormatDate(dateStr, 'dd.MM.yyyy');
  };

  const formatDateLong = (dateStr) => {
    return safeFormatDate(dateStr, 'EEEE, d. MMMM yyyy');
  };

  // Filter projects by selected customer
  const filteredProjects = filters.customer_id
    ? projects.filter(p => p.customer_id === filters.customer_id)
    : projects;

  const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
    <div className="card-glass p-5">
      <div className="flex items-center gap-4">
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            color === 'indigo'
              ? 'bg-indigo-500/20 text-indigo-400'
              : color === 'lime'
              ? 'bg-lime-500/20 text-lime-400'
              : color === 'amber'
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-emerald-500/20 text-emerald-400'
          }`}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-3xl font-heading font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Berichte</h1>
          <p className="text-muted-foreground mt-1">Detaillierte Projektzeit-Auswertung für Rechnungsstellung</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setMonthlyOpen(true)}
            data-testid="monthly-reports-button"
            className="border-border text-foreground"
          >
            <FileText className="w-4 h-4 mr-2" />
            Monatsauswertungen (PDF)
          </Button>
          <Button
            onClick={handleExport}
            data-testid="export-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV exportieren
          </Button>
        </div>
      </div>

      <MonthlyReportsDialog open={monthlyOpen} onOpenChange={setMonthlyOpen} api={api} />

      {/* Period Presets */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap gap-2"
      >
        {Object.entries(periodPresets).map(([key, preset]) => (
          <Button
            key={key}
            variant={periodPreset === key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriodPreset(key)}
            className={periodPreset === key 
              ? 'bg-indigo-500 text-white' 
              : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'
            }
          >
            {preset.label}
          </Button>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-glass p-4 md:p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-lg font-medium text-foreground">Filter</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {/* Start Date */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Von</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="start-date-filter"
                  className="w-full justify-start bg-input border-border text-foreground hover:bg-card"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {filters.start_date
                    ? safeFormatDate(filters.start_date, 'd. MMM yyyy')
                    : 'Startdatum'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.start_date ? new Date(filters.start_date) : undefined}
                  onSelect={(date) => {
                    setPeriodPreset('custom');
                    setFilters({ ...filters, start_date: date ? format(date, 'yyyy-MM-dd') : '' });
                  }}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Bis</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="end-date-filter"
                  className="w-full justify-start bg-input border-border text-foreground hover:bg-card"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {filters.end_date
                    ? safeFormatDate(filters.end_date, 'd. MMM yyyy')
                    : 'Enddatum'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.end_date ? new Date(filters.end_date) : undefined}
                  onSelect={(date) => {
                    setPeriodPreset('custom');
                    setFilters({ ...filters, end_date: date ? format(date, 'yyyy-MM-dd') : '' });
                  }}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Customer Filter */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Kunde</Label>
            <Select
              value={filters.customer_id || 'all'}
              onValueChange={(value) => setFilters({ 
                ...filters, 
                customer_id: value === 'all' ? '' : value,
                project_id: '' // Reset project when customer changes
              })}
            >
              <SelectTrigger
                data-testid="customer-filter"
                className="bg-input border-border text-foreground"
              >
                <SelectValue placeholder="Alle" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Alle Kunden</SelectItem>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Project Filter */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Projekt</Label>
            <Select
              value={filters.project_id || 'all'}
              onValueChange={(value) => setFilters({ ...filters, project_id: value === 'all' ? '' : value })}
            >
              <SelectTrigger
                data-testid="project-filter"
                className="bg-input border-border text-foreground"
              >
                <SelectValue placeholder="Alle" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Alle Aufträge</SelectItem>
                {filteredProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* User Filter */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Mitarbeiter</Label>
            <UserSelect
              users={users}
              value={filters.user_id || 'all'}
              onChange={(value) => setFilters({ ...filters, user_id: value === 'all' ? '' : value })}
              includeAllOption
              allLabel="Alle Mitarbeiter"
              placeholder="Alle"
              className="w-full"
            />
          </div>

          {/* Group By */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Gruppieren nach</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="bg-input border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="project">Projekt</SelectItem>
                <SelectItem value="user">Mitarbeiter</SelectItem>
                <SelectItem value="date">Datum</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Billing Status Filter */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Abrechnungsstatus</Label>
            <Select
              value={filters.billing_status || 'all'}
              onValueChange={(value) => setFilters({ ...filters, billing_status: value === 'all' ? '' : value })}
            >
              <SelectTrigger
                data-testid="billing-status-filter"
                className="bg-input border-border text-foreground"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="open">
                  <span className="flex items-center gap-2">
                    <CircleDot className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    Offen
                  </span>
                </SelectItem>
                <SelectItem value="billed">
                  <span className="flex items-center gap-2">
                    <CircleDollarSign className="w-3 h-3 text-green-600 dark:text-green-400" />
                    Abgerechnet
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </motion.div>

      {/* Summary Stats */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <StatCard
                icon={Clock}
                label="Zeitstunden"
                value={`${detailedReport?.total_hours?.toFixed(1) || 0}h`}
                subtext="Tatsächliche Arbeitszeit"
                color="lime"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}>
              <StatCard
                icon={TrendingUp}
                label="Gesamtstunden"
                value={`${detailedReport?.total_hours_with_surcharge?.toFixed(1) || 0}h`}
                subtext={detailedReport?.surcharge_hours > 0 ? `+${detailedReport.surcharge_hours.toFixed(1)}h Zuschläge` : "Inkl. Zuschläge"}
                color="emerald"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <StatCard
                icon={CircleDot}
                label="Offen"
                value={`${detailedReport?.open_hours?.toFixed(1) || 0}h`}
                subtext={`${detailedReport?.open_entries || 0} Einträge`}
                color="amber"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <StatCard
                icon={CircleDollarSign}
                label="Abgerechnet"
                value={`${detailedReport?.billed_hours?.toFixed(1) || 0}h`}
                subtext={`${detailedReport?.billed_entries || 0} Einträge`}
                color="emerald"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <StatCard
                icon={FileText}
                label="Einträge"
                value={detailedReport?.total_count || 0}
                color="indigo"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <StatCard
                icon={Receipt}
                label="Tagespauschalen"
                value={detailedReport?.daily_rate_count || 0}
                color="amber"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <StatCard
                icon={FolderKanban}
                label="Gruppen"
                value={detailedReport?.group_count || 0}
                subtext={groupBy === 'project' ? 'Aufträge' : groupBy === 'user' ? 'Mitarbeiter' : 'Tage'}
                color="indigo"
              />
            </motion.div>
          </div>

          {/* View Mode Tabs */}
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
            <TabsList className="bg-card/50 border border-border">
              <TabsTrigger value="grouped" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                <LayoutList className="w-4 h-4 mr-2" />
                Gruppiert
              </TabsTrigger>
              <TabsTrigger value="table" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                <Table className="w-4 h-4 mr-2" />
                Tabelle
              </TabsTrigger>
            </TabsList>

            {/* Grouped View */}
            <TabsContent value="grouped" className="mt-4 space-y-4">
              {detailedReport?.grouped_data?.map((group, index) => {
                const groupKey = groupBy === 'date' ? group.date : groupBy === 'project' ? group.project_id : group.user_id;
                const isExpanded = expandedGroups[groupKey];

                return (
                  <motion.div
                    key={groupKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="card-glass overflow-hidden"
                  >
                    {/* Group Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-accent/20 transition-colors"
                      onClick={() => toggleGroup(groupKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            groupBy === 'project' ? 'bg-emerald-500/20 text-emerald-400' :
                            groupBy === 'user' ? 'bg-indigo-500/20 text-indigo-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {groupBy === 'project' ? <FolderKanban className="w-5 h-5" /> :
                             groupBy === 'user' ? <Users className="w-5 h-5" /> :
                             <CalendarDays className="w-5 h-5" />}
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-foreground">
                              {groupBy === 'project' ? group.project_name :
                               groupBy === 'user' ? group.user_name :
                               formatDateLong(group.date)}
                            </h3>
                            {groupBy === 'project' && group.customer_name && (
                              <p className="text-sm text-muted-foreground">{group.customer_name}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">
                              Zeitstunden: <span className="text-foreground font-mono">{group.total_hours.toFixed(1)}h</span>
                            </p>
                            <p className="text-xl font-mono font-bold text-lime-600 dark:text-lime-400">
                              {group.total_hours_with_surcharge?.toFixed(1) || group.total_hours.toFixed(1)}h
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {group.entries.length} Einträge
                              {group.daily_rate_count > 0 && ` • ${group.daily_rate_count} TP`}
                              {group.total_hours_with_surcharge > group.total_hours && (
                                <span className="text-amber-600 dark:text-amber-400 ml-1">
                                  (+{(group.total_hours_with_surcharge - group.total_hours).toFixed(1)}h)
                                </span>
                              )}
                            </p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Entries */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-border"
                        >
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Datum</th>
                                  {groupBy !== 'user' && (
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Mitarbeiter</th>
                                  )}
                                  {groupBy !== 'project' && (
                                    <>
                                      <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Projekt</th>
                                      <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Kunde</th>
                                    </>
                                  )}
                                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Aktivität</th>
                                  <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Zeitstd.</th>
                                  <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Zuschlag</th>
                                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Gesamt</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-white/5">
                                {group.entries.map((entry, idx) => (
                                  <tr key={entry.id || idx} className="hover:bg-accent/20">
                                    <td className="p-3 text-sm text-foreground whitespace-nowrap">
                                      {formatDate(entry.date)}
                                    </td>
                                    {groupBy !== 'user' && (
                                      <td className="p-3 text-sm text-muted-foreground">{entry.user_name}</td>
                                    )}
                                    {groupBy !== 'project' && (
                                      <>
                                        <td className="p-3 text-sm text-muted-foreground">{entry.project_name}</td>
                                        <td className="p-3 text-sm text-muted-foreground">{entry.customer_name}</td>
                                      </>
                                    )}
                                    <td className="p-3 text-sm text-muted-foreground max-w-xs truncate">
                                      {entry.description || '-'}
                                    </td>
                                    <td className="p-3 text-center">
                                      {entry.billing_status === 'billed' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                          <CircleDollarSign className="w-3 h-3" />
                                          Abgerechnet
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                          <CircleDot className="w-3 h-3" />
                                          Offen
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                      <span className="text-sm font-mono text-muted-foreground">
                                        {entry.hours}h
                                      </span>
                                      {entry.is_daily_rate && (
                                        <span className="ml-2 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                          TP
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3 text-center whitespace-nowrap">
                                      {entry.surcharge_percent > 0 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                          +{entry.surcharge_percent}% {entry.surcharge_label}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                      <span className="text-sm font-mono font-semibold text-lime-600 dark:text-lime-400">
                                        {entry.total_hours_with_surcharge?.toFixed(2) || entry.hours}h
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-muted/50">
                                <tr>
                                  <td colSpan={groupBy === 'project' ? 4 : groupBy === 'user' ? 5 : 6} className="p-3 text-sm font-medium text-muted-foreground">
                                    Summe
                                  </td>
                                  <td className="p-3 text-right">
                                    <span className="text-sm font-mono text-muted-foreground">
                                      {group.total_hours.toFixed(1)}h
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    {group.total_hours_with_surcharge > group.total_hours && (
                                      <span className="text-amber-600 dark:text-amber-400 text-xs">
                                        +{(group.total_hours_with_surcharge - group.total_hours).toFixed(1)}h
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    <span className="text-lg font-mono font-bold text-lime-600 dark:text-lime-400">
                                      {group.total_hours_with_surcharge?.toFixed(1) || group.total_hours.toFixed(1)}h
                                    </span>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </TabsContent>

            {/* Table View */}
            <TabsContent value="table" className="mt-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-glass overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Datum</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Mitarbeiter</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Kunde</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Projekt</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Aktivität</th>
                        <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Zeitstd.</th>
                        <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Zuschlag</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Gesamt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {detailedReport?.entries?.map((entry, idx) => (
                        <tr key={entry.id || idx} className="hover:bg-accent/20">
                          <td className="p-3 text-sm text-foreground whitespace-nowrap">
                            {formatDate(entry.date)}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{entry.user_name}</td>
                          <td className="p-3 text-sm text-muted-foreground">{entry.customer_name}</td>
                          <td className="p-3 text-sm text-muted-foreground">{entry.project_name}</td>
                          <td className="p-3 text-sm text-muted-foreground max-w-xs truncate">
                            {entry.description || '-'}
                          </td>
                          <td className="p-3 text-center">
                            {entry.billing_status === 'billed' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                <CircleDollarSign className="w-3 h-3" />
                                Abgerechnet
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                <CircleDot className="w-3 h-3" />
                                Offen
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <span className="text-sm font-mono text-muted-foreground">
                              {entry.hours}h
                            </span>
                            {entry.is_daily_rate && (
                              <span className="ml-2 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                TP
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center whitespace-nowrap">
                            {entry.surcharge_percent > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                +{entry.surcharge_percent}% {entry.surcharge_label}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <span className="text-sm font-mono font-semibold text-lime-600 dark:text-lime-400">
                              {entry.total_hours_with_surcharge?.toFixed(2) || entry.hours}h
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50">
                      <tr>
                        <td colSpan={6} className="p-3 text-sm font-medium text-muted-foreground">
                          Gesamtsumme ({detailedReport?.total_count || 0} Einträge)
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm font-mono text-muted-foreground">
                            {detailedReport?.total_hours?.toFixed(1) || 0}h
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {detailedReport?.surcharge_hours > 0 && (
                            <span className="text-amber-600 dark:text-amber-400 text-xs">
                              +{detailedReport.surcharge_hours.toFixed(1)}h
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-lg font-mono font-bold text-lime-600 dark:text-lime-400">
                            {detailedReport?.total_hours_with_surcharge?.toFixed(1) || 0}h
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>

          {/* Empty State */}
          {detailedReport?.total_entries === 0 && (
            <div className="text-center py-16">
              <BarChart3 className="w-12 h-12 text-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Keine Daten gefunden</h3>
              <p className="text-muted-foreground">Für die ausgewählten Filter wurden keine Einträge gefunden.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
